import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, FlexBox, Modal, Text } from '@eltoro-ui/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { faStars } from '@fortawesome/pro-solid-svg-icons';
import QuestionBlock from 'Pages/CampaignCreation/components/GeneratedCampaign/GeneratedCampaignContent/EditWithAIModal/QuestionBlock';
import AIAnswer from 'Pages/CampaignCreation/components/GeneratedCampaign/GeneratedCampaignContent/EditWithAIModal/AIAnswer';
import AIPromptContainer from 'Pages/CampaignCreation/components/GeneratedCampaign/GeneratedCampaignContent/EditWithAIModal/AIPromptContainer';
import AITyping from 'Pages/CampaignCreation/components/GeneratedCampaign/GeneratedCampaignContent/EditWithAIModal/AITyping';
import ConversationFail from 'Pages/CampaignCreation/components/GeneratedCampaign/GeneratedCampaignContent/EditWithAIModal/ConversationFail';
import SkeletonConnecting from 'Pages/CampaignCreation/components/GeneratedCampaign/GeneratedCampaignContent/EditWithAIModal/SkeletonConnecting';
import { getBannerPrompts, sendPrompt } from 'Requests/Request_Methods/campaignMethods';
import { AIRespondStatus, GenerationTypeEnum } from 'enums';
import type { GenBannerType, TRootState } from 'types';
import { AIGenerationResponseType } from 'types';
import '../GeneratedCreatives.scss';

type EditWithAiModalProps = {
  editableBanner: GenBannerType | null;
  onClose: () => void;
  onUpdateCampaign: () => Promise<void>;
  goToBannerEditor: VoidFunction;
};

const EditWithAiModal = ({
  editableBanner,
  onClose,
  onUpdateCampaign,
  goToBannerEditor,
}: EditWithAiModalProps) => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { userData } = useSelector((state: TRootState) => state.userReducer);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const userName = `${userData.first_name} ${userData.last_name}`;
  const [pendingAnswer, setPendingAnswer] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [maxSizeReached, setMaxSizeReached] = useState(false);
  const [promptData, setPromptData] = useState('');
  const [hasErr, setHasErr] = useState(false);
  const [hasAiError, setHasAiError] = useState(false);
  const [aiConnectionExists, setAiConnectionExists] = useState<null | boolean>(null);
  const [conversationData, setConversationData] = useState<AIGenerationResponseType[]>([]);
  const [disableSend, setDisableSend] = useState(false);

  const handleFocus = (promptValue: AIGenerationResponseType | null, success: boolean) => {
    if (promptValue && success) {
      setConversationData(prevState => [...prevState, promptValue]);
    }
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    inputRef.current?.focus();
  };
  const getPrompts = () => {
    if (campaignId && editableBanner) {
      getBannerPrompts(campaignId, editableBanner?.banner_uuid)
        .then(res => {
          setAiConnectionExists(true);
          if (res.data) setConversationData(res.data);
        })
        .catch(err => {
          setAiConnectionExists(false);
        });
    }
  };
  useEffect(() => {
    getPrompts();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView();
  }, [conversationData, pendingAnswer]);
  const hasApprovedAfterCompletedUntilIndex = (index: number): boolean => {
    const sliced = conversationData.slice(1, index);
    if (sliced.length === 0) return true;
    let lastApprovedIndex = -1;
    let lastCompletedIndex = -1;
    sliced.forEach((item, i) => {
      if (item?.status === AIRespondStatus.APPROVED) {
        lastApprovedIndex = i;
      }
      if (item?.status === AIRespondStatus.COMPLETED) {
        lastCompletedIndex = i;
      }
    });
    if (lastApprovedIndex === -1 && lastCompletedIndex === -1) return true;

    return lastApprovedIndex > lastCompletedIndex;
  };

  const retryPromptRequest = () => {
    if (editableBanner?.banner_uuid) {
      setPendingAnswer(true);
      sendPrompt(campaignId, editableBanner?.banner_uuid, promptData)
        .then((res: any) => {
          if (res.data) {
            setConversationData(prevState => [...prevState, res.data]);
          }
          if (hasErr) {
            setHasErr(false);
          }
        })
        .catch(err => {
          if (err.detail && err.detail === 'Max prompts reached') {
            setMaxSizeReached(true);
          } else if (
            err.detail &&
            (err.detail === 'Invalid response from AI' ||
              err.detail === 'Invalid response from AI in json')
          ) {
            setHasAiError(true);
          } else {
            setHasErr(true);
          }
        })
        .finally(() => {
          setPendingAnswer(false);
        });
    }
  };
  return (
    <Modal className="edit-creative-modal edit-with-ai-modal">
      <Button
        onClick={() => {
          onClose();
          setPromptData('');
        }}
        UNSAFE_className="close_cross"
      >
        <FontAwesomeIcon icon={faTimes} style={{ cursor: 'pointer', fontSize: '24px' }} />
      </Button>
      <FlexBox flexDirection="column" alignItems="center" UNSAFE_className="budget_wrapper">
        <FlexBox
          flexDirection="column"
          UNSAFE_style={{ width: '100%', position: 'relative' }}
          gap="20px"
        >
          <FlexBox gap="8px" alignItems="center" justifyContent="center">
            <FontAwesomeIcon icon={faStars} color="#007B94" style={{ fontSize: '16px' }} />
            <Text
              on="white"
              weight="bold"
              textAlign="center"
              UNSAFE_style={{ color: '#2B1F0A', fontSize: 16 }}
            >
              Edit Banner with AI
            </Text>
          </FlexBox>
          {aiConnectionExists && editableBanner ? (
            <FlexBox flexDirection="column" gap="16px" UNSAFE_className="chat-container">
              {conversationData.map((conversation, index) => {
                return (
                  <FlexBox
                    flexDirection="column"
                    gap="16px"
                    UNSAFE_className="chat-content"
                    key={conversation.gen_banner_uuid + index}
                  >
                    {conversation.generation_type !== GenerationTypeEnum.INITIAL &&
                      conversation.prompt_data && (
                        <QuestionBlock
                          userPhoto={userData.profile_pic}
                          userName={userName}
                          promptData={conversation.prompt_data}
                          onRetry={retryPromptRequest}
                          type={conversation.generation_type}
                        />
                      )}
                    {conversation.response_data && (
                      <AIAnswer
                        status={conversation.status}
                        onFocus={handleFocus}
                        onClose={onClose}
                        type={conversation.generation_type}
                        imgUrl={conversation.presigned_url}
                        userName={userName}
                        intent={conversation.response_data.intent}
                        approveLatest={hasApprovedAfterCompletedUntilIndex(index)}
                        bannerId={conversation.gen_banner_uuid}
                        promptId={conversation.gen_banner_prompt_uuid}
                        onUpdateCampaign={onUpdateCampaign}
                        conversationData={conversationData}
                        index={index}
                        hasAIError={hasAiError}
                        setDisableSend={setDisableSend}
                        disableBtn={disableSend}
                        parent_gen_banner_prompt_uuid={conversation.parent_gen_banner_prompt_uuid}
                      />
                    )}
                  </FlexBox>
                );
              })}
              {promptData && (pendingAnswer || hasErr || hasAiError) && (
                <FlexBox flexDirection="column" gap="16px" UNSAFE_className="chat-content">
                  <QuestionBlock
                    userPhoto={userData.profile_pic}
                    userName={userName}
                    promptData={promptData}
                    hasErr={hasErr}
                    onRetry={retryPromptRequest}
                    type={null}
                  />
                </FlexBox>
              )}
              {pendingAnswer && (
                <FlexBox flexDirection="column" gap="16px" UNSAFE_className="chat-content">
                  <AITyping />
                </FlexBox>
              )}
              {!pendingAnswer && maxSizeReached && (
                <FlexBox flexDirection="column" gap="16px" UNSAFE_className="chat-content">
                  <QuestionBlock
                    userPhoto={userData.profile_pic}
                    userName={userName}
                    promptData={promptData}
                    onRetry={retryPromptRequest}
                    type={null}
                  />
                  <AIAnswer
                    status={null}
                    onFocus={handleFocus}
                    onClose={onClose}
                    type={null}
                    userName={userName}
                    onUpdateCampaign={onUpdateCampaign}
                    conversationData={conversationData}
                    approveLatest={hasApprovedAfterCompletedUntilIndex(conversationData.length)}
                    index={conversationData.length}
                    setDisableSend={setDisableSend}
                    goToBannerEditor={goToBannerEditor}
                    disableBtn={disableSend}
                    parent_gen_banner_prompt_uuid={null}
                  />
                </FlexBox>
              )}
              {!pendingAnswer && hasAiError && (
                <FlexBox flexDirection="column" gap="16px" UNSAFE_className="chat-content">
                  <AIAnswer
                    status={AIRespondStatus.GENERATION_FAILED}
                    onFocus={handleFocus}
                    userName={userName}
                    type={null}
                    onClose={onClose}
                    onUpdateCampaign={onUpdateCampaign}
                    conversationData={conversationData}
                    approveLatest={hasApprovedAfterCompletedUntilIndex(conversationData.length)}
                    index={conversationData.length}
                    hasAIError={hasAiError}
                    setDisableSend={setDisableSend}
                    disableBtn={disableSend}
                    parent_gen_banner_prompt_uuid={null}
                  />
                </FlexBox>
              )}
              <div ref={bottomRef} />
            </FlexBox>
          ) : aiConnectionExists === false ? (
            <ConversationFail onReload={getPrompts} />
          ) : (
            <div style={{ height: '80vh' }}>
              <SkeletonConnecting />
            </div>
          )}
        </FlexBox>
      </FlexBox>
      {aiConnectionExists && (
        <AIPromptContainer
          textAreaRef={inputRef}
          onFocus={handleFocus}
          setConversationData={setConversationData}
          banner_uuid={editableBanner?.banner_uuid}
          setPendingAnswer={setPendingAnswer}
          setMaxSizeReached={setMaxSizeReached}
          setPromptData={setPromptData}
          setHasErr={setHasErr}
          setHasAiErr={setHasAiError}
          disableSend={disableSend}
        />
      )}
    </Modal>
  );
};

export default EditWithAiModal;
